import { useMemo } from 'react';
import { useProfiles } from '@/features/users/hooks';
import { useItems } from '@/features/inventory/hooks';
import { useOfficeAvatars } from '@/features/digitalOffice/hooks';
import { seededAvatar, type AvatarConfig } from '@/features/digitalOffice/avatars';
import { useFloorWorkstations } from './hooks';
import type { FloorWorkstation } from './types';

export interface WorkPerson {
  id: string;
  name: string;
  email: string | null;
  role: string;
  jobTitle: string | null;
  isActive: boolean;
  avatarUrl: string | null;
  config: AvatarConfig;
}

export interface WorkItemRef {
  id: string;
  name: string;
  sku: string;
}

/**
 * Everything the manager views need to turn ids into names and faces:
 * profiles + office avatars, catalog items and workstations.
 */
export function useWorkLookups() {
  const { data: profiles = [], isLoading: profilesLoading } = useProfiles();
  const { data: avatars = [] } = useOfficeAvatars();
  const { data: items = [], isLoading: itemsLoading } = useItems();
  const { data: stations = [], isLoading: stationsLoading } = useFloorWorkstations();

  return useMemo(() => {
    const avatarByUser = new Map(avatars.map((a) => [a.userId, a.config]));
    const people = new Map<string, WorkPerson>();
    for (const p of profiles) {
      people.set(p.id, {
        id: p.id,
        name: p.full_name || p.email || 'Teammate',
        email: p.email ?? null,
        role: p.role,
        jobTitle: p.job_title ?? null,
        isActive: p.is_active,
        avatarUrl: p.avatar_url ?? null,
        config: avatarByUser.get(p.id) ?? seededAvatar(p.id),
      });
    }
    const itemMap = new Map<string, WorkItemRef>();
    for (const i of items) itemMap.set(i.id, { id: i.id, name: i.name, sku: i.sku });
    const stationMap = new Map<string, FloorWorkstation>();
    for (const s of stations) stationMap.set(s.id, s);

    const person = (id: string | null | undefined): WorkPerson =>
      (id && people.get(id)) || {
        id: id ?? '',
        name: 'Teammate',
        email: null,
        role: 'employee',
        jobTitle: null,
        isActive: false,
        avatarUrl: null,
        config: seededAvatar(id ?? 'x'),
      };

    return {
      loading: profilesLoading || itemsLoading || stationsLoading,
      people,
      peopleList: [...people.values()].sort((a, b) => a.name.localeCompare(b.name)),
      items: itemMap,
      stations: stationMap,
      stationList: stations,
      person,
      personName: (id: string | null | undefined) => person(id).name,
      itemName: (id: string | null | undefined) => (id ? (itemMap.get(id)?.name ?? 'Unknown item') : null),
      itemSku: (id: string | null | undefined) => (id ? (itemMap.get(id)?.sku ?? null) : null),
      stationName: (id: string | null | undefined) => (id ? (stationMap.get(id)?.name ?? 'Unknown station') : null),
    };
  }, [profiles, avatars, items, stations, profilesLoading, itemsLoading, stationsLoading]);
}

export type WorkLookups = ReturnType<typeof useWorkLookups>;
